<template>
  <div class="bg-white pb-8 sm:pb-12 relative overflow-hidden">
    
    <!-- 背景装饰形状 -->
    <div class="floating-bg-shapes">

      <div class="bg-shape bg-gradient-to-br from-yellow-100/30 to-orange-100/30 w-40 h-40 rounded-full absolute -bottom-20 -right-20"></div>
    </div>
    
    <!-- 头部标题区域 -->
    <div class="w-full h-full relative z-10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 text-center mb-8 sm:mb-12">
        <h1 class="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
          {{ t('solution.title') }}
        </h1>
        <p class="text-xs sm:text-sm lg:text-base text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
          {{ t('solution.description') }}
        </p>
      </div>

      <!-- 6个圆形图标分类 -->
       <div class="w-full bg-[#f8f4e9]">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 sm:py-12 px-4 sm:px-6 lg:px-8 mb-12 sm:mb-16">
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8">
            <div
              v-for="(category, index) in categories"
              :key="index"
              class="text-center group cursor-pointer"
            >
              <div class="relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto mb-3 sm:mb-4 group relative overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-orange-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full"></div>
                <div class="w-full h-full rounded-full overflow-hidden border-2 border-amber-200 group-hover:border-amber-300 transition-colors relative z-10">
                  <img
                  :src="getCategoryImage(index)"
                  :alt="category"
                  class="w-full h-full object-cover rounded-full"
                />
                </div>
              </div>
              <h3 class="text-xs sm:text-sm font-medium text-gray-800">{{ category }}</h3>
            </div>
          </div>
        </div>
      </div>

      <!-- 餐厅食物包装解决方案示例 -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div class="relative h-80 sm:h-96 lg:h-[32rem] xl:h-[36rem] rounded-lg overflow-hidden mb-6 sm:mb-8 group">
          
          <img
            :src="getExampleImage()"
            :alt="t('solution.example.title')"
            class="w-full h-full object-cover object-center group-hover:scale-125 transition-transform duration-700"
          />
          <div class="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center z-10">
            <div class="text-white max-w-2xl px-6 sm:px-8 lg:px-12 xl:px-16">
              <h2 class="text-base sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-4 sm:mb-6 leading-tight">
                {{ t('solution.example.title') }}
              </h2>
              <p class="text-xs sm:text-sm lg:text-base xl:text-lg leading-relaxed sm:leading-loose lg:leading-loose max-w-lg">
                {{ t('solution.example.description') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 8个步骤流程 -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-10">
        <div class="max-w-2xl">
          <ol class="space-y-4 pl-6">
            <li
              v-for="(step, index) in processSteps"
              :key="index"
              class="flex items-start text-gray-800"
            >
              <span class="text-base sm:text-sm lg:text-sm font-medium mr-2">{{ index + 1 }}.</span>
              <span class="text-base sm:text-sm lg:text-sm leading-relaxed">{{ step }}</span>
            </li>
          </ol>
        </div>
      </div>

      <!-- 产品展示区 -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8 md:mb-12 lg:mb-16">
        <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 md:gap-6 lg:gap-8">
          <div v-for="(product, index) in products" :key="index" class="group">
            <div class="bg-gray-50 rounded-lg overflow-hidden mb-2 sm:mb-3 md:mb-4 hover:shadow-sm transition-all duration-300 border border-gray-100">
              <img
                :src="getProductImage(index)"
                class="w-full h-32 sm:h-40 md:h-48 lg:h-56 object-cover group-hover:scale-125 transition-transform duration-700"
              />
            </div>
            <h3 class="text-center font-medium text-xs sm:text-sm md:text-base lg:text-base">{{ product.title }}</h3>
          </div>
        </div>
      </div>

      <!-- 查看更多按钮 -->
      <div class="text-center px-4 sm:px-6 lg:px-8">
        <a-button
          type="primary"
          class="!rounded-button bg-green-600 hover:bg-green-700 h-10 sm:h-12 px-6 sm:px-8 text-sm sm:text-base lg:text-lg w-auto sm:w-auto"
          @click="navigateToProduct"
        >
          {{ t('solution.viewMore') }}
        </a-button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

// 使用国际化
const { t, locale, messages } = useI18n()

// 路由
const router = useRouter()

// 跳转到产品页面
const navigateToProduct = () => {
  router.push('/product')
}



// 分类数据 - 使用国际化数据
const categories = computed(() => {
  try {
    const currentMessages = messages.value[locale.value]
    if (currentMessages && currentMessages.solution && currentMessages.solution.categories) {
      return currentMessages.solution.categories
    }
    // 如果国际化数据不可用，使用固定数据作为后备
    return [
      "餐厅打包",
      "食品配送", 
      "咖啡店",
      "商场与超市",
      "服装包装",
      "航空与动车"
    ]
  } catch (error) {
    console.error('Error getting categories:', error)
    return []
  }
})

// 流程步骤 - 使用国际化数据
const processSteps = computed(() => {
  try {
    const currentMessages = messages.value[locale.value]
    if (currentMessages && currentMessages.solution && currentMessages.solution.process) {
      return currentMessages.solution.process
    }
    // 如果国际化数据不可用，使用固定数据作为后备
    return [
      "与我们建立联系",
      "提供您的需求：包装类型、包装规格、包装材质要求、需求数量、印刷内容，如您没有目标需求，我们可以根据您的产品为您提供方案",
      "确认需求，我们快速为您提供报价",
      "核对单价、优化单价",
      "制作样板进行核实",
      "签订合同，确认订单",
      "生产大货，大货检测",
      "货运到您提货地点"
    ]
  } catch (error) {
    console.error('Error getting process steps:', error)
    return []
  }
})

// 产品数据 - 固定4个产品
const products = computed(() => [
  { id: 1 },
  { id: 2 },
  { id: 3 },
  { id: 4 }
])

// 动态获取图片路径的函数
const getCategoryImage = (index) => {
  const categoryImages = [
    'jimeng-2025-08-27-7640-生成一个场景，国外餐厅里有一张桌子，桌子上面摆放着很多外卖牛皮纸包装.png', // 餐厅打包
    'jimeng-2025-08-27-5826-生成一个场景，外国的外卖员手拿牛皮纸包装袋为顾客派送.png', // 食品配送
    'jimeng-2025-08-27-2899-在咖啡店里，桌面摆着一排牛皮纸手提袋.jpg', // 咖啡店
    'jimeng-2025-08-27-2620-背景是国外超时里面，背景是食物零食货架，前面摆放着牛皮纸打包袋，产品偏小，背景偏....jpg', // 商场与超市
    'jimeng-2025-08-27-3769-生成一张装着服装的牛皮纸信封袋.png', // 服装包装
    'jimeng-2025-08-27-6711-飞机上，国外乘务员手棒着白牛皮纸呕吐袋.jpg' // 航空与动车
  ]
  
  try {
    const imageName = categoryImages[index] || categoryImages[0]
    return new URL(`../../../assets/solution/${imageName}`, import.meta.url).href
  } catch (error) {
    console.warn(`Category icon not found for index: ${index}`)
    return ''
  }
}

// 获取示例图片
const getExampleImage = () => {
  try {
    return new URL('../../../assets/solution/jimeng-2025-08-27-9724-生成一张宽屏图，背景是餐厅，前面摆放着一些牛皮纸袋、包装盒、纸杯，产品在右下角，....jpg', import.meta.url).href
  } catch (error) {
    console.warn('Banner image not found')
    return ''
  }
}

// 获取产品图片
const getProductImage = (index) => {
  const productImages = [
    'jimeng-2025-08-27-2015-白色背景，大小不同规格，带颜色的牛皮纸手提袋.png', // 手提袋产品
    'jimeng-2025-08-27-7781-纯白色背景，大小不同规格，摆放着牛皮纸法棍面包袋.png', // 法棍面包袋
    'jimeng-2025-08-27-5807-纯白色背景，摆放着牛皮纸餐具包装袋，包装袋有锯齿.png', // 餐具包装袋
    'jimeng-2025-08-27-6800-白色背景，大小不同规格，透明开窗牛皮纸面包袋，桌面有面包.png', // 面包袋
  ]
  
  try {
    const imageName = productImages[index] || productImages[0]
    return new URL(`../../../assets/solution/${imageName}`, import.meta.url).href
  } catch (error) {
    console.warn(`Product image not found for index: ${index}`)
    return ''
  }
}
</script>

<style scoped>
/* 背景装饰 */
.floating-bg-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}



/* 移动端响应式优化 */
@media (max-width: 768px) {
  /* 产品展示区移动端优化 */
  .grid {
    gap: 1rem;
  }
  
  /* 移动端标题字体优化 */
  h3.text-sm {
    font-size: 0.875rem !important;
    line-height: 1.25rem !important;
  }
  
  /* 移动端按钮优化 */
  .ant-btn {
    height: 2.5rem;
    font-size: 0.875rem;
    padding: 0 1.5rem;
    min-width: 120px;
  }
  
  /* 移动端图片高度调整 */
  .h-48 {
    height: 12rem;
  }
  

}

/* 小屏手机优化 */
@media (max-width: 480px) {
  .grid {
    gap: 0.75rem;
  }
  
  /* 小屏手机标题字体进一步优化 */
  h3.text-sm {
    font-size: 0.8rem !important;
    line-height: 1.125rem !important;
  }
  
  /* 小屏手机按钮优化 */
  .ant-btn {
    height: 2.25rem;
    font-size: 0.8rem;
    padding: 0 1.25rem;
    min-width: 110px;
  }
  
  /* 小屏手机图片高度调整 */
  .h-48 {
    height: 10rem;
  }
}

/* 平板端适配 */
@media (min-width: 769px) and (max-width: 1024px) {
  .grid {
    gap: 1.5rem;
  }
  
  /* 平板端图片高度调整 */
  .sm\:h-56 {
    height: 14rem;
  }
}

/* 移动端字体大小优化 */
@media (max-width: 768px) {
  /* 移动端列表项字体优化 */
  .text-base {
    font-size: 0.875rem !important;
    line-height: 1.25rem !important;
  }
  
  .leading-relaxed {
    line-height: 1.4 !important;
  }
}

/* 小屏手机优化 */
@media (max-width: 480px) {
  .text-base {
    font-size: 0.8rem !important;
    line-height: 1.125rem !important;
  }
  
  .leading-relaxed {
    line-height: 1.3 !important;
  }
}

/* 超小屏优化 */
@media (max-width: 360px) {
  .text-base {
    font-size: 0.75rem !important;
    line-height: 1rem !important;
  }
}

/* 移动端触摸优化 */
@media (max-width: 1024px) {
  .group:active {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }
  
  .ant-btn:active {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }
}

/* 确保可访问性 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
